// src/services/videosApi.js
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../api/baseQuery';

// API fallback messages are English-only. Translate at toast/call sites via useVideosTranslation.

// Videos Status Enum
export const VideosStatus = {
  Draft: 0,
  Active: 1,
  Completed: 2,
  Archived: 3,
};

// Question Type Enum (matching survey structure)
export const QuestionType = {
  MultipleChoice: 0,
  SingleChoice: 1,
  YesNo: 2,
  Rating: 3,
};

// Using centralized baseQueryWithReauth from api/baseQuery.js
export const videosApi = createApi({
  reducerPath: 'videosApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Videos', 'VideosStats', 'VideoProcessing'],
  endpoints: (builder) => ({
    // Get all video sets (admin sees all, tenant admin sees only their own)
    getVideoss: builder.query({
      query: ({ pageNumber = 1, pageSize = 8, keyword = '', sortBy, sortOrder } = {}) => {
        // Backend expects "page" (0-indexed) instead of "pageNumber" (1-indexed)
        const page = pageNumber - 1; // Convert 1-indexed to 0-indexed
        
        const queryParams = {
          page: page.toString(),
          pageSize: pageSize.toString(),
          ...(keyword && keyword.trim() && { keyword: keyword.trim() }),
          ...(sortBy && { sortBy }),
          ...(sortOrder && { sortOrder }),
        };

        console.log('🔍 VideoSet API Query Params:', queryParams);
        
        return {
          url: '/videoset/list',
          method: 'GET',
          params: queryParams,
        };
      },
      transformResponse: (response) => {
        console.log('✅ VideoSet List API Response:', response);
        
        if (!response) {
          return {
            value: {
              videoSets: [],
              totalCount: 0,
            },
            isSuccess: false,
            errors: [],
            validationErrors: [],
          };
        }

        return {
          value: {
            videoSets: response.value?.videoSets || [],
            totalCount: response.value?.totalCount || 0,
          },
          isSuccess: response.isSuccess,
          errors: response.errors || [],
          validationErrors: response.validationErrors || [],
        };
      },
      providesTags: (result) =>
        result?.value?.videoSets
          ? [
              ...result.value.videoSets.map(({ id }) => ({ type: 'Videos', id })),
              { type: 'Videos', id: 'LIST' },
            ]
          : [{ type: 'Videos', id: 'LIST' }],
    }),

    // Get single video set detail (web admin)
    getVideosById: builder.query({
      query: (id) => ({
        url: `/videoset/${id}`,
        method: 'GET',
      }),
      transformResponse: (response) => {
        console.log('✅ VideoSet Detail API Response:', response);

        if (!response) {
          return {
            value: null,
            isSuccess: false,
            errors: [],
            validationErrors: [],
          };
        }

        return {
          value: response.value || null,
          isSuccess: response.isSuccess,
          errors: response.errors || [],
          validationErrors: response.validationErrors || [],
        };
      },
      providesTags: (result, error, id) => [{ type: 'Videos', id }],
    }),

    // Create video set
    createVideos: builder.mutation({
      query: (videoSetData) => {
        console.log('🔥 VideoSet Create API - Request Data:', videoSetData);
        
        // Expected structure:
        // {
        //   name: string,
        //   description: string,
        //   tenantIds: string[],
        //   items: [{ title, description, tempVideoUrl, order }],
        //   categoryId: string (optional),
        //   tags: string[] (optional)
        // }
        
        return {
          url: '/videoset/create',
          method: 'POST',
          body: videoSetData,
        };
      },
      transformResponse: (response) => {
        console.log('✅ VideoSet Create API Response:', response);
        
        if (!response) {
          return {
            isSuccess: true,
            message: 'Video set created successfully',
            value: true
          };
        }
        
        return response;
      },
      transformErrorResponse: (response) => {
        console.error('❌ VideoSet Create API Error:', response);
        return {
          status: response.status,
          message: response.data?.message || 'An error occurred while creating the video set',
          errors: response.data?.errors || [],
          validationErrors: response.data?.validationErrors || []
        };
      },
      invalidatesTags: [{ type: 'Videos', id: 'LIST' }],
    }),

    // Update videos
    updateVideos: builder.mutation({
      query: ({ id, ...videosData }) => {
        console.log('🔥 Videos Update API - Request Data:', { id, ...videosData });
        return {
          url: `/videos/update`,
          method: 'PUT',
          body: { id, ...videosData },
        };
      },
      transformResponse: (response, meta) => {
        console.log('✅ Videos Update API Response:', response);
        console.log('📡 Videos Update Meta:', meta);
        
        // Handle empty response (204 No Content)
        if (meta?.response?.status === 204) {
          return {
            isSuccess: true,
            message: 'Video module updated successfully',
            value: true
          };
        }
        
        // Handle null response
        if (!response) {
          return {
            isSuccess: true,
            message: 'Video module updated successfully',
            value: true
          };
        }
        
        return response;
      },
      transformErrorResponse: (response) => {
        console.error('❌ Videos Update API Error:', response);
        return {
          status: response.status,
          message: response.data?.message || 'An error occurred while updating the video module',
          errors: response.data?.errors || [],
          validationErrors: response.data?.validationErrors || []
        };
      },
      invalidatesTags: (result, error, { id }) => [
        { type: 'Videos', id },
        { type: 'Videos', id: 'LIST' },
      ],
    }),

    // Delete videos
    deleteVideos: builder.mutation({
      query: (id) => {
        console.log('🔥 Videos Delete API - ID:', id);
        return {
          url: `/videos/${id}`,
          method: 'DELETE',
        };
      },
      transformResponse: (response, meta) => {
        console.log('✅ Videos Delete API Response:', response);
        
        // Handle 204 No Content
        if (meta?.response?.status === 204) {
          return {
            isSuccess: true,
            message: 'Video module deleted successfully',
          };
        }
        
        return response || { isSuccess: true, message: 'Deleted successfully' };
      },
      transformErrorResponse: (response) => {
        console.error('❌ Videos Delete API Error:', response);
        return {
          status: response.status,
          message: response.data?.message || 'An error occurred while deleting the video module',
          errors: response.data?.errors || [],
        };
      },
      invalidatesTags: [{ type: 'Videos', id: 'LIST' }],
    }),

    // Upload temp video (temporary upload before final save)
    uploadTempVideo: builder.mutation({
      query: (file) => {
        console.log('🔥 Temp Video Upload API - Building Request');
        console.log('📁 File:', file);

        const formData = new FormData();
        formData.append('file', file);

        return {
          url: '/videoset/upload-temp',
          method: 'POST',
          body: formData,
          formData: true,
        };
      },
      transformResponse: (response) => {
        console.log('✅ Temp Video Upload API Response:', response);
        
        // Expected response structure:
        // {
        //   "url": "string",
        //   "previewUrl": "string",
        //   "fileName": "string",
        //   "fileSize": 0
        // }
        
        if (!response) {
          throw new Error('Empty response from server');
        }

        const payload = response.value ?? response;

        // Extract data from response
        const url = payload.url;
        const previewUrl = payload.previewUrl;
        const fileName = payload.fileName;
        const fileSize = payload.fileSize;
        
        console.log('🔍 Temp Video Upload Extraction:', {
          url,
          previewUrl,
          fileName,
          fileSize
        });
        
        // Validate required fields
        if (!url) {
          console.error('❌ Temp Video Upload - Missing URL in response');
          throw new Error('Upload successful but missing URL in response');
        }
        
        // Return normalized response
        return {
          url,
          previewUrl: previewUrl || null,
          fileName,
          fileSize,
          isSuccess: true
        };
      },
      transformErrorResponse: (response) => {
        console.error('❌ Temp Video Upload API Error:', response);
        return {
          status: response.status,
          message: response.data?.message || 'An error occurred while uploading the video',
          errors: response.data?.errors || [],
          validationErrors: response.data?.validationErrors || []
        };
      },
    }),

    // Upload videos video (legacy - kept for backward compatibility)
    addVideosVideo: builder.mutation({
      query: (formData) => {
        console.log('🔥 Videos Video API - Building Request');
        console.log('📁 API FormData:', formData);

        return {
          url: '/videos/upload-video',
          method: 'POST',
          body: formData,
          formData: true,
        };
      },
      transformResponse: (response) => {
        console.log('✅ Videos Video API Response:', response);
        
        // Extract URL from response
        let extractedUrl = null;
        
        if (response?.value?.url) {
          extractedUrl = response.value.url;
        } else if (response?.value && typeof response.value === 'string') {
          extractedUrl = response.value;
        } else if (response?.url) {
          extractedUrl = response.url;
        } else if (response?.data?.url) {
          extractedUrl = response.data.url;
        }
        
        console.log('🔍 Video URL Extraction:', {
          extractedUrl: extractedUrl,
          isSuccess: response?.isSuccess
        });
        
        // Validate success response has URL
        if (response?.isSuccess && !extractedUrl) {
          console.error('❌ Video Upload - Successful response missing URL');
          throw new Error('Upload successful but missing URL in response');
        }
        
        // Return normalized response
        return {
          ...response,
          value: extractedUrl || response?.value,
          isSuccess: response?.isSuccess || false,
          errors: response?.errors || [],
          validationErrors: response?.validationErrors || []
        };
      },
      transformErrorResponse: (response) => {
        console.error('❌ Videos Video API Error:', response);
        return response;
      },
      invalidatesTags: ['Videos'],
    }),

    // Video processing status panel (admin)
    getVideoProcessingStatus: builder.query({
      query: ({ status } = {}) => ({
        url: '/videoset/processing-status',
        method: 'GET',
        ...(status != null && { params: { status: String(status) } }),
      }),
      transformResponse: (response) => {
        if (!response) {
          return {
            value: {
              items: [],
              totalCount: 0,
              uploadedCount: 0,
              processingCount: 0,
              readyCount: 0,
              failedCount: 0,
            },
            isSuccess: false,
            errors: [],
          };
        }

        return {
          value: response.value || {
            items: [],
            totalCount: 0,
            uploadedCount: 0,
            processingCount: 0,
            readyCount: 0,
            failedCount: 0,
          },
          isSuccess: response.isSuccess,
          errors: response.errors || [],
        };
      },
      providesTags: [{ type: 'VideoProcessing', id: 'STATUS' }],
    }),

    getVideoTestReport: builder.query({
      query: (videoTestId) => ({
        url: `/videoset/test/${videoTestId}/report`,
        method: 'GET',
      }),
      transformResponse: (response) => ({
        value: response?.value || null,
        isSuccess: response?.isSuccess ?? false,
        errors: response?.errors || [],
      }),
      providesTags: (result, error, videoTestId) => [
        { type: 'Videos', id: `REPORT-${videoTestId}` },
      ],
    }),

    retryVideoItem: builder.mutation({
      query: (videoSetItemId) => ({
        url: `/videoset/item/${videoSetItemId}/retry`,
        method: 'POST',
      }),
      transformResponse: (response) => response || { isSuccess: true },
      invalidatesTags: [
        { type: 'VideoProcessing', id: 'STATUS' },
        { type: 'Videos', id: 'LIST' },
      ],
    }),

    // Get videos statistics
    getVideosStats: builder.query({
      query: () => ({
        url: '/videos/statistics',
        method: 'GET',
      }),
      transformResponse: (response) => {
        console.log('✅ Videos Stats API Response:', response);
        
        if (!response) {
          return {
            value: null,
            isSuccess: false,
            errors: [],
          };
        }

        return {
          value: response.value || null,
          isSuccess: response.isSuccess,
          errors: response.errors || [],
        };
      },
      providesTags: ['VideosStats'],
    }),
  }),
});

export const {
  useGetVideossQuery,
  useGetVideosByIdQuery,
  useCreateVideosMutation,
  useUpdateVideosMutation,
  useDeleteVideosMutation,
  useUploadTempVideoMutation,
  useAddVideosVideoMutation,
  useGetVideosStatsQuery,
  useGetVideoProcessingStatusQuery,
  useRetryVideoItemMutation,
  useGetVideoTestReportQuery,
} = videosApi;

export const {
  reducer: videosReducer,
  middleware: videosMiddleware,
  reducerPath: videosReducerPath,
} = videosApi;
